/**
 * The async seam for a procedural texture that builds as a worker job. A lookup
 * is ready from the cache, or pending with a build its holders start and
 * release. A build is keyed on its content, not its scene, so an edit that
 * leaves a texture unchanged reuses it instead of building it again.
 */
import { perObject } from '../../utils/perObject';
import { unlessAllocationFails } from './pixelAllocation';
import { allocationLabel, findTypedSubResource } from './resolveProceduralSubResource';
import { cachedProceduralTexture, cacheProceduralTexture, clearProceduralTextures, } from './proceduralTextureCache';
import { textureWork } from './textureWork';
/**
 * Written by `joinBuild`. An entry leaves when its build settles, its last holder aborts it, or
 * the cache resets.
 */
const builds = new Map();
/**
 * Aborts every build in flight and drops every cached texture. Only tests call it, to start from
 * an empty cache: the one reset, owned beside both, so no test resets half of it.
 */
export function resetProceduralTextures() {
    for (const build of builds.values())
        build.controller.abort();
    builds.clear();
    clearProceduralTextures();
}
/**
 * Resolves `ref` as an inline `[sub_resource]` of `typeName`. Null for every other
 * reference form, and for a resource the plan declines, which leaves the caller's
 * other paths untouched. `sourcesOf` names the objects the plan reads from outside the resource:
 * the plan builds again when one of them is another object. A ready texture is borrowed: pin `key`, never dispose it.
 */
export function resolveProceduralSubResourceAsync(ref, internalResources, typeName, plan, sourcesOf) {
    const resource = findTypedSubResource(ref, internalResources, typeName);
    if (!resource)
        return null;
    const build = plannedBuild(resource, internalResources, plan, sourcesOf(resource.data));
    if (!build)
        return null;
    const key = build.contentKey + (build.derived?.suffix ?? '');
    const texture = cachedProceduralTexture(key);
    if (texture)
        return { status: 'ready', texture, key };
    const label = allocationLabel(typeName, resource.id);
    return { status: 'pending', key, start: (runner) => joinDerived(key, build, label, runner) };
}
/**
 * Written by `plannedBuild`, once per parse, sub-resource and set of sources. Each consumer of a
 * texture looks it up on every parse, and a plan decodes the resource and serialises its input.
 */
const plansByParse = perObject(() => new Map());
function plannedBuild(resource, internalResources, plan, sources) {
    const plans = plansByParse(internalResources);
    const held = plans.get(resource);
    if (held && held.sources.every((source, index) => source === sources[index]))
        return held.plan;
    const planned = { sources, plan: plan(resource.data) };
    plans.set(resource, planned);
    return planned.plan;
}
/**
 * `joinBuild` on the built texture's key, and the derived texture of its result. A plan with none
 * is the build itself. Holders of a built texture and of its derivations share one run.
 */
function joinDerived(key, plan, label, runner) {
    const handle = joinBuild(plan.contentKey, plan, label, runner);
    const { derived } = plan;
    if (!derived)
        return handle;
    const derivedTexture = (built) => built && (cachedProceduralTexture(key) ?? cacheDerived(key, label, derived.derive, built));
    return { ...handle, settled: handle.settled.then(derivedTexture) };
}
function cacheDerived(key, label, derive, built) {
    const texture = unlessAllocationFails(label, () => derive(built));
    if (texture)
        cacheProceduralTexture(key, texture);
    return texture;
}
function joinBuild(key, plan, label, runner) {
    const inFlight = builds.get(key);
    // A build can land between a holder's lookup and its start. A second run would
    // repeat the work and replace the cached texture that other holders draw.
    const landed = inFlight ? undefined : cachedProceduralTexture(key);
    if (landed)
        return { settled: Promise.resolve(landed), release: () => { } };
    const build = inFlight ?? startBuild(key, plan, label, runner);
    build.holders += 1;
    let released = false;
    return {
        settled: build.settled,
        release: () => {
            if (released)
                return;
            released = true;
            build.holders -= 1;
            // A microtask, not now: StrictMode and a re-render release and take a build
            // again in one task, and that must not cancel it.
            queueMicrotask(() => {
                if (build.holders > 0 || builds.get(key) !== build)
                    return;
                builds.delete(key);
                build.controller.abort();
            });
        },
    };
}
function startBuild(key, plan, label, runner) {
    const controller = new AbortController();
    const endWork = textureWork.begin();
    const settled = runner.run(plan.job, plan.input, controller.signal).then((output) => {
        const texture = plan.wrap(output);
        cacheProceduralTexture(key, texture);
        return texture;
    }, (error) => {
        if (controller.signal.aborted)
            return null;
        return unlessAllocationFails(label, () => {
            throw error;
        });
    });
    const build = { holders: 0, settled, controller };
    builds.set(key, build);
    const finish = () => {
        if (builds.get(key) === build)
            builds.delete(key);
        endWork();
    };
    settled.then(finish, finish);
    return build;
}
//# sourceMappingURL=proceduralBuilds.js.map